import { useNavigate } from '@tanstack/react-router';
import { motion, useReducedMotion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import welcomeBackground from '@/assets/images/welcome-bg.webp';
import linearLogo from '@/assets/images/whats-new/integrations/linear.jpg';
import { Box, Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import { Routes } from '@/types/routes';
import { trackEvent } from '@/utils/analytics';

import type { ReactNode } from 'react';

type WelcomeItem =
  | 'integrations'
  | 'routines'
  | 'meetings'
  | 'research'
  | 'chat';

function trackWelcomeItemClick(item: WelcomeItem) {
  trackEvent('WELCOME_ITEM_CLICKED', { item });
}

const CARD_HOVER_CLASS = cn(
  'transition duration-200 hover:bg-surface-hover',
  'motion-safe:[@media(hover:hover)_and_(pointer:fine)]:hover:rotate-0',
  'motion-safe:[@media(hover:hover)_and_(pointer:fine)]:hover:-translate-y-1.5'
);

const Bar = ({ className }: { className?: string }) => (
  <Box className={cn('h-1 rounded-full bg-grayA-3', className)} />
);

const Vignette = ({ children }: { children: ReactNode }) => (
  <YStack aria-hidden className="h-[4.5rem] w-32 justify-center gap-2">
    {children}
  </YStack>
);

const IntegrationTile = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <XStack
    center
    className={cn(
      'size-9 rounded-lg border border-grayA-3 bg-grayA-2',
      className
    )}
  >
    {children}
  </XStack>
);

const IntegrationsVignette = () => (
  <Vignette>
    <XStack center className="gap-1">
      <IntegrationTile className="-rotate-[4deg]">
        <Icons.gmail className="size-4" />
      </IntegrationTile>
      <IntegrationTile className="size-10 -translate-y-1.5">
        <Icons.slack className="size-5" />
      </IntegrationTile>
      <IntegrationTile className="rotate-[4deg]">
        <img
          src={linearLogo}
          alt="Linear"
          className="size-4 rounded object-contain"
          draggable={false}
        />
      </IntegrationTile>
    </XStack>
  </Vignette>
);

const RoutinesVignette = () => (
  <Vignette>
    <XStack className="items-center gap-2">
      <Icons.repeat2 className="size-3.5 shrink-0 text-foreground-muted" />
      <Bar className="h-1.5 w-16" />
    </XStack>
    <Box className="h-px bg-grayA-3" />
    <YStack className="gap-2">
      <XStack className="items-center gap-2">
        <Icons.mail className="size-3 shrink-0 text-foreground-muted" />
        <Bar className="w-full" />
      </XStack>
      <XStack className="items-center gap-2">
        <Icons.mail className="size-3 shrink-0 text-foreground-muted" />
        <Bar className="w-2/3" />
      </XStack>
    </YStack>
  </Vignette>
);

const MeetingsVignette = () => (
  <Vignette>
    <XStack className="items-center gap-2">
      <XStack className="-space-x-1">
        <Box className="size-4 rounded-full border border-surface bg-gray-4" />
        <Box className="size-4 rounded-full border border-surface bg-gray-4" />
        <Box className="size-4 rounded-full border border-surface bg-gray-4" />
      </XStack>
      <Bar className="h-1.5 w-12" />
    </XStack>
    <YStack className="gap-1.5 rounded-md border border-grayA-3 px-2 py-1.5">
      <Bar className="w-full" />
      <Bar className="w-3/4" />
      <Bar className="w-1/2" />
    </YStack>
  </Vignette>
);

const ResearchVignette = () => (
  <Vignette>
    <XStack className="items-center gap-2 rounded-full border border-grayA-3 px-2.5 py-1.5">
      <Icons.search className="size-3 shrink-0 text-foreground-muted" />
      <Bar className="w-2/3" />
    </XStack>
    <YStack className="gap-2 pl-2.5">
      <XStack className="items-center gap-2">
        <Icons.www className="size-3 shrink-0 text-foreground-muted" />
        <Bar className="w-full" />
      </XStack>
      <XStack className="items-center gap-2">
        <Icons.www className="size-3 shrink-0 text-foreground-muted" />
        <Bar className="w-3/4" />
      </XStack>
    </YStack>
  </Vignette>
);

type WelcomeCardProps = {
  label: string;
  className?: string;
  onClick: () => void;
  children: ReactNode;
};

const WelcomeCard = ({
  label,
  className,
  onClick,
  children,
}: WelcomeCardProps) => (
  <button
    type="button"
    className={cn(
      'flex w-full max-w-[13.75rem] flex-col items-center justify-center gap-5 rounded-2xl border border-grayA-3 bg-surface px-5 py-6',
      'font-sans text-base text-foreground text-center leading-snug',
      'focus-ring',
      CARD_HOVER_CLASS,
      className
    )}
    onClick={onClick}
  >
    {children}
    <span>{label}</span>
  </button>
);

export const WelcomePage = () => {
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion() ?? false;

  const startNewChat = (item: 'research' | 'chat') => {
    trackWelcomeItemClick(item);
    navigate({ to: Routes.Chats as string, search: { new: true } });
  };

  return (
    <Box className="relative size-full overflow-hidden">
      <img
        aria-hidden
        src={welcomeBackground}
        alt=""
        draggable={false}
        className="pointer-events-none absolute inset-x-0 bottom-0 w-full select-none"
      />
      <YStack className="relative z-10 size-full items-center overflow-y-auto px-8 pb-16 pt-14">
        <motion.div
          initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: shouldReduceMotion ? 0 : 0.45,
            ease: 'easeOut',
          }}
          className="flex w-full flex-col items-center gap-9"
        >
          <YStack className="max-w-2xl items-center gap-4 text-center">
            <h1 className="font-serif text-4xl text-foreground">
              Welcome to Littlebird
            </h1>
            <p className="text-lg text-foreground-muted leading-snug">
              Your assistant for everything happening in your work and life.
              <br />
              Connect your everyday tools and it takes it from there.
            </p>
          </YStack>
          <Grid className="w-full max-w-[28.75rem] grid-cols-2 gap-5 lg:max-w-[59.5rem] lg:grid-cols-4 lg:gap-6">
            <WelcomeCard
              label="Connect your tools"
              className="-rotate-2"
              onClick={() => {
                trackWelcomeItemClick('integrations');
                navigate({ to: Routes.Integrations as string });
              }}
            >
              <IntegrationsVignette />
            </WelcomeCard>
            <WelcomeCard
              label="Automate the busywork with Routines"
              className="rotate-[1.5deg]"
              onClick={() => {
                trackWelcomeItemClick('routines');
                navigate({ to: Routes.Routines as string });
              }}
            >
              <RoutinesVignette />
            </WelcomeCard>
            <WelcomeCard
              label="Get briefed before every meeting"
              className="-rotate-[1.5deg]"
              onClick={() => {
                trackWelcomeItemClick('meetings');
                navigate({ to: Routes.Meetings as string });
              }}
            >
              <MeetingsVignette />
            </WelcomeCard>
            <WelcomeCard
              label="Research anything on the web"
              className="rotate-2"
              onClick={() => startNewChat('research')}
            >
              <ResearchVignette />
            </WelcomeCard>
          </Grid>
          <p className="text-base text-foreground-muted">
            or just{' '}
            <button
              type="button"
              className="underline underline-offset-4 hover:text-foreground focus-ring rounded-sm"
              onClick={() => startNewChat('chat')}
            >
              chat
            </button>
            …
          </p>
        </motion.div>
      </YStack>
    </Box>
  );
};
